import React, {useState} from "react"
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import {DatePicker} from "@mui/x-date-pickers/DatePicker"
import {LocalizationProvider} from "@mui/x-date-pickers"
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns"
import {differenceInCalendarDays, format} from "date-fns"

import {useAgent} from "@indebted/hooks/Agent"
import {
	api,
	ClientCasesCaseType,
	ClientCasesClient,
	ClientCasesExportColumn,
	ClientCasesExportType,
} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

const DATE_FORMAT = "yyyy-MM-dd"

type CasesExportPanelProps = {
	exportTypes: ClientCasesExportType[]
	exportColumns: ClientCasesExportColumn[]
	maxDateRange: number
	clients: ClientCasesClient[]
	caseTypes: ClientCasesCaseType[]
}

function triggerDownload(url: string) {
	const link = document.createElement("a")
	link.href = url
	link.rel = "noopener"
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}

function CasesExportPanel({exportTypes, exportColumns, maxDateRange, clients, caseTypes}: CasesExportPanelProps) {
	const [agentEmail] = useAgent()
	const {notification} = useNotification()

	const [open, setOpen] = useState(false)
	const [exportType, setExportType] = useState(exportTypes[0]?.ID ?? "")
	const [clientID, setClientID] = useState("")
	const [caseType, setCaseType] = useState("")
	const [dateFrom, setDateFrom] = useState<Date | null>(null)
	const [dateTo, setDateTo] = useState<Date | null>(null)
	const [columns, setColumns] = useState<string[]>(exportColumns.map((c) => c.ID))
	const [submitting, setSubmitting] = useState(false)

	const toggleColumn = (id: string) =>
		setColumns((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))

	const close = () => {
		if (!submitting) setOpen(false)
	}

	const rangeError = (() => {
		if (!dateFrom || !dateTo) return ""
		if (differenceInCalendarDays(dateTo, dateFrom) < 0) return "The end date must be after the start date"
		if (differenceInCalendarDays(dateTo, dateFrom) + 1 > maxDateRange) {
			return `The date range must not exceed ${maxDateRange} days`
		}
		return ""
	})()

	const canSubmit = !submitting && !!exportType && !!dateFrom && !!dateTo && columns.length > 0 && !rangeError

	const submit = () => {
		if (!canSubmit || !dateFrom || !dateTo) return
		setSubmitting(true)

		const payload = {
			AgentEmail: agentEmail ?? "",
			ExportType: exportType,
			ClientID: clientID || undefined,
			CaseType: caseType || undefined,
			DateFrom: format(dateFrom, DATE_FORMAT),
			DateTo: format(dateTo, DATE_FORMAT),
			Columns: columns,
		}

		api.cases
			.export(payload)
			.then((res) => {
				triggerDownload(res.DownloadURL)
				notification.success("Export ready — downloading")
				setOpen(false)
			})
			.catch((err) => notification.error(err?.Message ?? "Failed to generate export"))
			.finally(() => setSubmitting(false))
	}

	return (
		<>
			<Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => setOpen(true)}>
				Export
			</Button>

			<Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
				<DialogTitle>Export cases</DialogTitle>
				<DialogContent>
					<Stack spacing={3.5} sx={{mt: 1}}>
						<FormControl size="small" fullWidth>
							<InputLabel id="export-type-label">Export</InputLabel>
							<Select
								labelId="export-type-label"
								label="Export"
								value={exportType}
								onChange={(e: SelectChangeEvent) => setExportType(e.target.value)}
							>
								{exportTypes.map((t) => (
									<MenuItem key={t.ID} value={t.ID}>
										{t.Label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl size="small" fullWidth>
							<InputLabel id="export-client-label" shrink>
								Client
							</InputLabel>
							<Select
								labelId="export-client-label"
								label="Client"
								value={clientID}
								onChange={(e: SelectChangeEvent) => setClientID(e.target.value)}
								displayEmpty
							>
								<MenuItem value="">All clients</MenuItem>
								{clients.map((c) => (
									<MenuItem key={c.ID} value={c.ID}>
										{c.Name} ({c.CountryCode})
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormControl size="small" fullWidth>
							<InputLabel id="export-casetype-label" shrink>
								Case type
							</InputLabel>
							<Select
								labelId="export-casetype-label"
								label="Case type"
								value={caseType}
								onChange={(e: SelectChangeEvent) => setCaseType(e.target.value)}
								displayEmpty
							>
								<MenuItem value="">All case types</MenuItem>
								{caseTypes.map((ct) => (
									<MenuItem key={ct.ID} value={ct.ID}>
										{ct.Label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<Stack direction={{xs: "column", sm: "row"}} spacing={2}>
								<DatePicker
									label="From"
									value={dateFrom}
									onChange={setDateFrom}
									format="dd MMM yyyy"
									slotProps={{textField: {size: "small", fullWidth: true}}}
								/>
								<DatePicker
									label="To"
									value={dateTo}
									onChange={setDateTo}
									format="dd MMM yyyy"
									slotProps={{
										textField: {
											size: "small",
											fullWidth: true,
											error: !!rangeError,
											helperText: rangeError,
										},
									}}
								/>
							</Stack>
						</LocalizationProvider>

						<Box>
							<Typography variant="body2" sx={{mb: 1, color: "text.secondary"}}>
								Columns
							</Typography>
							<Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
								{exportColumns.map((c) => {
									const selected = columns.includes(c.ID)
									return (
										<Chip
											key={c.ID}
											label={c.Label}
											onClick={() => toggleColumn(c.ID)}
											color={selected ? "primary" : "default"}
											variant={selected ? "filled" : "outlined"}
										/>
									)
								})}
							</Box>
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{px: 3, pb: 2}}>
					<Button onClick={close} disabled={submitting}>
						Cancel
					</Button>
					<Button variant="contained" onClick={submit} disabled={!canSubmit}>
						{submitting ? <CircularProgress size={20} color="inherit" /> : "Export CSV"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export {CasesExportPanel}
